import { DEFAULT_LOCALE, LOCALES } from "./localization.constants";
import { useLocale } from "./use-locale";

export function isLocalizableHref(href: string) {
    // only paths starting with a single slash to prevent localizing URLs
    // starts with a slash, followed by the end of the string or anything that's not a slash
    return /^\/($|[^\/])/.test(href);
}

export function isAlreadyLocalizedHref(href: string) {
    return (
        LOCALES.some((locale) => href.startsWith(`/${locale}/`)) ||
        LOCALES.some((locale) => href === `/${locale}`)
    );
}

export interface UseLocalizedHrefOptions {
    absolute?: boolean;
    supportedLocales?: string[];
}

export function useLocalizedHref(
    href: string,
    opts: UseLocalizedHrefOptions = {}
) {
    const { supportedLocales, absolute } = opts;
    const locale = useLocale();

    if (absolute) {
        href = absoluteToRelativeHref(href);
    }

    return getLocalizedHref({ locale, href, supportedLocales });
}

export interface GetLocaledHrefParams {
    locale: string;
    href: string;
    supportedLocales?: string[];
}

export function getLocalizedHref({
    locale,
    href,
    supportedLocales
}: GetLocaledHrefParams) {
    if (
        locale === DEFAULT_LOCALE ||
        isAlreadyLocalizedHref(href) ||
        !isLocalizableHref(href)
    ) {
        return href;
    }

    if (supportedLocales) {
        return supportedLocales.includes(locale) ? `/${locale}${href}` : href;
    }

    return `/${locale}${href}`;
}

export function absoluteToRelativeHref(href: string) {
    const baseUrl = /^https?:\/\/(www\.)?atlassian\.com/;
    if (!baseUrl.test(href)) {
        return href;
    }
    return href.replace(baseUrl, "");
}

function getTrelloPath(trelloRoute: string) {
    if (trelloRoute === "/") {
        return "";
    }
    return trelloRoute;
}

export function useLocalizedHrefTrello(trelloRoute: string) {
    const trelloBaseUrl = "https://trello.com";
    const locale = useLocale();
    const trelloPath = getTrelloPath(trelloRoute);
    if (locale === DEFAULT_LOCALE) {
        return `${trelloBaseUrl}${trelloPath}`;
    }
    return `${trelloBaseUrl}/${locale}${trelloPath}`;
}
